import { useEffect, useState } from 'react';
import LicenseInfo from './components/LicenseInfo';
import CompanyDetails from './components/CompanyDetails';
import AddressSection from './components/AddressSection';
import ActivitySection from './components/ActivitySection';
import ManagerSection from './components/ManagerSection';
import RemarksSection from './components/RemarksSection';
import MissionSection from './components/MissionSection';
import Footer from '../../components/feature/Footer';
import LanguageSwitcher from '../../components/feature/LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../mocks/translations';

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4">
          <div className="flex items-center justify-between gap-1 sm:gap-2">
            <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 flex-shrink-0 min-w-0 max-w-[50%] sm:max-w-none">
              <img 
                src="https://public.readdy.ai/ai/img_res/e536622a-0947-470a-853b-93e6ddce8b2f.png" 
                alt="QIUTOMS ENERGY Logo" 
                className="h-6 sm:h-8 lg:h-12 w-auto object-contain flex-shrink-0"
              />
              <div className="min-w-0 flex-shrink">
                <h1 className="text-[10px] sm:text-sm lg:text-xl font-bold text-gray-900 truncate leading-tight">QIUTOMS ENERGY</h1>
                <p className="text-[8px] sm:text-[10px] lg:text-xs text-gray-600 leading-tight">FZ-LLC</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 flex-shrink-0">
              <a href="#license" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">{t.nav.licenseInfo}</a>
              <a href="#company" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">{t.nav.company}</a>
              <a href="#activity" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">{t.nav.activity}</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">{t.nav.contact}</a>
              <LanguageSwitcher />
            </div>

            {/* Mobile Menu Button & Language Switcher */}
            <div className="flex lg:hidden items-center gap-0.5 sm:gap-1 flex-shrink-0">
              <LanguageSwitcher />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-1 sm:p-1.5 text-gray-700 hover:text-teal-600 cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg sm:text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col gap-3">
                <a 
                  href="#license" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer py-2"
                >
                  {t.nav.licenseInfo}
                </a>
                <a 
                  href="#company" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer py-2"
                >
                  {t.nav.company}
                </a>
                <a 
                  href="#activity" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer py-2"
                >
                  {t.nav.activity}
                </a>
                <a 
                  href="#contact" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer py-2"
                >
                  {t.nav.contact}
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 lg:pt-40 pb-12 sm:pb-16 lg:pb-20 px-4 sm:px-6 overflow-hidden min-h-[600px] lg:min-h-[700px]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/11837a11433ac26530a778ea5b99a305.jpg"
            alt="Energy Industry"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4 sm:mb-6">
              <i className="ri-shield-check-line text-white text-sm sm:text-base"></i>
              <span className="text-xs sm:text-sm font-medium text-white">{t.hero.badge}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight px-4 drop-shadow-lg">
              {t.hero.title}
            </h2>
            <p className="text-base sm:text-lg text-gray-100 mb-6 sm:mb-8 leading-relaxed px-4">
              {t.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
              <a href="#license" className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-center">
                {t.hero.viewLicense}
              </a>
              <a href="#contact" className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-white/20 backdrop-blur-sm text-white border-2 border-white/50 rounded-lg font-medium hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer text-center">
                {t.hero.getInTouch}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="py-12 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <MissionSection />
        </div>
      </section>

      {/* License Info Section */}
      <section id="license" className="py-12 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <LicenseInfo />
        </div>
      </section>

      {/* Company Details Section */}
      <section id="company" className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <CompanyDetails />
        </div>
      </section>

      {/* Address Section */}
      <section id="address" className="py-12 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <AddressSection />
        </div>
      </section>

      {/* Activity Section */}
      <section id="activity" className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-7xl mx-auto">
          <ActivitySection />
        </div>
      </section>

      {/* Manager Section */}
      <section id="manager" className="py-12 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <ManagerSection />
        </div>
      </section>

      {/* Remarks Section */}
      <section id="remarks" className="py-12 sm:py-20 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <RemarksSection />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-teal-600 to-emerald-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">{t.contact.title}</h2>
          <p className="text-base sm:text-lg text-teal-50 mb-6 sm:mb-8">
            {t.contact.description}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="w-full sm:w-auto px-6 py-3 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <p className="text-sm text-teal-100 mb-1">{t.contact.licenseNumber}</p>
              <p className="text-lg font-bold text-white">5037142</p>
            </div>
            <div className="w-full sm:w-auto px-6 py-3 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <p className="text-sm text-teal-100 mb-1">{t.contact.type}</p>
              <p className="text-lg font-bold text-white">Freezone</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
